import type {
  Order,
  OrderRequestBody,
  ConfirmOrder,
  ExtraFees,
  ExtraFeesPrices,
} from 'types/orders';
import { OrderStatus } from 'types/orders';
import { endpoints } from 'store/endpoints';
import type { SecondaryDriver } from 'store/slices/orders/orders.slice';
import type { PaginationParams } from 'store/slices/orders/actions';
import { axiosInstance } from './axiosInstance';

export type DropOffConfirmationPayload = {
  id: string;
  notes: string;
  millage: number;
  extraFees: ExtraFees;
  extraFeePrices: ExtraFeesPrices;
  files: Array<File>;
};
export class OrderService {
  static async refund(id: string, amount: number) {
    try {
      const response = await axiosInstance.post(endpoints.orders.refund(id), {
        amount,
      });
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async confirmPartiallyOrder(id: string, paymentMethodId: string) {
    try {
      await axiosInstance.post(endpoints.orders.confirmPartiallyOrder(id), {
        paymentMethodId,
      });
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async approveOrder(id: string) {
    try {
      const response = await axiosInstance.post(endpoints.orders.approve(id));
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async refuseOrder(id: string, note: string) {
    try {
      const response = await axiosInstance.post(endpoints.orders.refuse(id), {
        note,
      });
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async getPendingOrders(params: PaginationParams) {
    try {
      const response = await axiosInstance.get(endpoints.orders.pendingOrders, {
        params,
      });
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async switchDeposit(id: string) {
    try {
      const response = await axiosInstance.patch(
        endpoints.orders.switchDeposit(id),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async updateMultipleOrders(ids: Array<string>, body: Partial<Order>) {
    try {
      const response = await axiosInstance.patch<Array<Order>>(
        endpoints.orders.updateMultipleOrders(ids.join(',')),
        body,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async releaseSecurityDeposit(orderId: string) {
    try {
      const response = await axiosInstance.patch(
        endpoints.orders.releaseSecurityDeposit(orderId),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async updateSecurityDepositDocument(file: File, id: string) {
    try {
      const formData = new FormData();
      if (!(file instanceof File)) {
        return;
      }
      formData.append('file', file);
      const response = await axiosInstance.patch(
        endpoints.orders.updateSecurityDepositDocument(id),
        formData,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async attachSecondaryDriver(
    payload: SecondaryDriver | { secondaryDriverId: string },
    orderId: string,
  ) {
    try {
      const response = await axiosInstance.patch(
        endpoints.orders.attachSecondaryDriver(orderId),
        payload,
      );

      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async confirmDropOff(payload: DropOffConfirmationPayload) {
    try {
      const { id, files, ...rest } = payload;
      const formData = new FormData();
      files.forEach((item) => {
        formData.append('files', item);
      });
      formData.append('body', JSON.stringify(rest));
      const response = await axiosInstance.patch(
        endpoints.orders.confirmDropOff(id),
        formData,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async editOrder(payload: Partial<Order>) {
    try {
      const { id, ...rest } = payload;
      if (!id) {
        return;
      }
      const response = await axiosInstance.patch(
        endpoints.orders.opWithId(id),
        rest,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async removeOrderFile(id: string) {
    try {
      await axiosInstance.delete(endpoints.orders.removeOrderFile(id));
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async updateOrderFiles(file: File, id: string) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.patch(
        endpoints.orders.updateOrderFiles(id),
        formData,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async deleteOrder(id: string) {
    try {
      const response = await axiosInstance.delete(
        endpoints.orders.opWithId(id),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async getOrderById(id: string) {
    try {
      const response = await axiosInstance.get<Order>(
        endpoints.orders.opWithId(id),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async getAllOrders(params?: Record<keyof Order, string>) {
    try {
      const response = await axiosInstance.get(endpoints.orders.general, {
        params,
      });
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async createNewPendingOrder(body: OrderRequestBody) {
    try {
      const response = await axiosInstance.post<Order>(
        endpoints.orders.general,
        {
          ...body,
          status: OrderStatus.PENDING,
        },
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async confirmOrder(body: ConfirmOrder, files: Array<File>) {
    try {
      const { id, ...rest } = body;
      const formData = new FormData();
      files.forEach((item) => {
        formData.append('files', item);
      });
      formData.append('body', JSON.stringify(rest));
      const response = await axiosInstance.patch<Order>(
        endpoints.orders.confirmOrder(id),
        formData,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
}
